import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { LogOut, User, Building2, TrendingUp } from 'lucide-react';
import axios from 'axios';
import branding from '@/config/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

// Consistent color scheme for entity types
// Each entity type has ONE color used for both subtabs and tags
const ENTITY_COLORS = {
  events: {
    tab: 'bg-emerald-500',       // Subtab active background
    tabText: 'text-white',       // Subtab active text
    tagBg: 'bg-emerald-50',      // Tag background
    tagText: 'text-emerald-700', // Tag text
    tagBorder: 'border-emerald-200' // Tag border
  },
  investment_bankers: {
    tab: 'bg-purple-500',
    tabText: 'text-white',
    tagBg: 'bg-purple-50',
    tagText: 'text-purple-700',
    tagBorder: 'border-purple-200'
  },
  funds: {
    tab: 'bg-amber-500',
    tabText: 'text-white',
    tagBg: 'bg-amber-50',
    tagText: 'text-amber-700',
    tagBorder: 'border-amber-200'
  },
  angel_networks: {
    tab: 'bg-pink-500',
    tabText: 'text-white',
    tagBg: 'bg-pink-50',
    tagText: 'text-pink-700',
    tagBorder: 'border-pink-200'
  }
};

// Helper to get tag classes for an entity type
function getTagClasses(entityType) {
  const colors = ENTITY_COLORS[entityType];
  return `${colors.tagBg} ${colors.tagText} border ${colors.tagBorder}`;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [events, setEvents] = useState([]);
  const [investmentBankers, setInvestmentBankers] = useState([]);
  const [funds, setFunds] = useState([]);
  const [angelNetworks, setAngelNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [eventSubTab, setEventSubTab] = useState('');
  const [bankerSubTab, setBankerSubTab] = useState('');
  const [fundSubTab, setFundSubTab] = useState('');
  const [networkSubTab, setNetworkSubTab] = useState('');
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (!email) {
      navigate('/login');
      return;
    }

    fetchUserProfile(email);
    fetchDeals();
    fetchEvents();
    fetchInvestmentBankers();
    fetchFunds();
    fetchAngelNetworks();
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
      // Set ForeRunner 2025 as default, or first event if ForeRunner doesn't exist
      if (response.data.length > 0 && !eventSubTab) {
        const foreRunner = response.data.find(e => e.name === 'ForeRunner 2025');
        setEventSubTab(foreRunner ? foreRunner.name : response.data[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch events');
    }
  };

  const fetchInvestmentBankers = async () => {
    try {
      const response = await axios.get(`${API}/investment-bankers`);
      setInvestmentBankers(response.data);
      if (response.data.length > 0 && !bankerSubTab) {
        setBankerSubTab(response.data[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch investment bankers');
    }
  };

  const fetchFunds = async () => {
    try {
      const response = await axios.get(`${API}/funds`);
      setFunds(response.data);
      if (response.data.length > 0 && !fundSubTab) {
        setFundSubTab(response.data[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch funds');
    }
  };

  const fetchAngelNetworks = async () => {
    try {
      const response = await axios.get(`${API}/angel-networks`);
      setAngelNetworks(response.data);
      if (response.data.length > 0 && !networkSubTab) {
        setNetworkSubTab(response.data[0].name);
      }
    } catch (error) {
      console.error('Failed to fetch angel networks');
    }
  };

  const fetchUserProfile = async (email) => {
    try {
      const response = await axios.get(`${API}/profile?email=${email}`);
      const user = response.data;
      
      if (user.status === 'rejected') {
        navigate('/login');
        return;
      }
      
      if (!user.is_onboarded) {
        navigate('/onboarding');
        return;
      }
      
      setUserName(`${user.first_name} ${user.last_name}`);
      setIsAdmin(user.is_admin || false);
      setCanManageMembers(user.can_manage_members || false);
    } catch (error) {
      console.error('Failed to fetch profile');
    }
  };

  const fetchDeals = async () => {
    try {
      const userEmail = localStorage.getItem('user_email');
      const response = await axios.get(`${API}/deals`, {
        params: { user_email: userEmail }
      });
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Filter deals by tab, subtab, and search query
  const filteredDeals = (() => {
    let filtered = deals;
    
    if (activeTab === 'All') {
      // Show all deals
      filtered = deals;
    } else if (activeTab === 'Events') {
      filtered = deals.filter(deal => deal.type === 'Events' && deal.event_category === eventSubTab);
    } else if (activeTab === 'Investment Banker') {
      if (bankerSubTab) {
        filtered = deals.filter(deal => deal.investment_bankers?.includes(bankerSubTab));
      } else {
        filtered = deals.filter(deal => deal.type === 'Investment Banker' || (deal.investment_bankers && deal.investment_bankers.length > 0));
      }
    } else if (activeTab === 'Fund') {
      if (fundSubTab) {
        filtered = deals.filter(deal => deal.funds?.includes(fundSubTab));
      } else {
        filtered = deals.filter(deal => deal.type === 'Fund' || (deal.funds && deal.funds.length > 0));
      }
    } else if (activeTab === 'Angel Network') {
      if (networkSubTab) {
        filtered = deals.filter(deal => deal.angel_networks?.includes(networkSubTab));
      } else {
        filtered = deals.filter(deal => deal.type === 'Angel Network' || (deal.angel_networks && deal.angel_networks.length > 0));
      }
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(deal => 
        deal.name?.toLowerCase().includes(query) ||
        deal.brand_name?.toLowerCase().includes(query) ||
        deal.one_liner?.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query) ||
        deal.short_description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <p className="text-[#64748B]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex flex-col">
                <h1 className="font-heading text-xl font-bold text-brand">{branding.platformName}</h1>
                <span className="text-[10px] text-gray-500 tracking-wide">Powered by InMeSta Ecosystem</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {canManageMembers && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin')}
                  className={isAdmin ? "border-brand text-brand hover:bg-brand hover:text-white" : "border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"}
                >
                  {isAdmin ? 'Admin Dashboard' : 'Team Dashboard'}
                </Button>
              )}
              <div 
                className="flex items-center gap-2 border-l border-[#E2E8F0] pl-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate('/profile')}
                title="Go to Profile"
              >
                <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white font-semibold text-sm">
                  {userName ? userName.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className="text-sm font-medium text-[#0F172A]">{userName}</span>
              </div>
              <Button
                variant="ghost"
                data-testid="logout-btn"
                onClick={handleLogout}
                className="!bg-transparent text-[#64748B] hover:text-[#0F172A] hover:!bg-transparent"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="font-heading text-2xl font-bold text-[#0F172A] mb-2">DEALS</h2>
          <p className="text-[#64748B]">Browse and commit to exclusive investment opportunities</p>
        </div>

        {/* Filters */}
        <Tabs value={activeTab} onValueChange={(val) => { 
          setActiveTab(val); 
          if (val === 'Events' && events.length > 0) {
            const foreRunner = events.find(e => e.name === 'ForeRunner 2025');
            setEventSubTab(foreRunner ? foreRunner.name : events[0].name);
          }
          if (val === 'Investment Banker' && investmentBankers.length > 0 && !bankerSubTab) {
            setBankerSubTab(investmentBankers[0].name);
          }
          if (val === 'Fund' && funds.length > 0 && !fundSubTab) {
            setFundSubTab(funds[0].name);
          }
          if (val === 'Angel Network' && angelNetworks.length > 0 && !networkSubTab) {
            setNetworkSubTab(angelNetworks[0].name);
          }
        }} className="mb-6">
          <TabsList className="bg-white border border-[#E2E8F0] h-12">
            <TabsTrigger value="All" className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold">All</TabsTrigger>
            <TabsTrigger value="Events" className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold">Events</TabsTrigger>
            <TabsTrigger value="Investment Banker" className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold">Investment Banker</TabsTrigger>
            <TabsTrigger value="Angel Network" className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold">Angel Network</TabsTrigger>
            <TabsTrigger value="Fund" className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold">Fund</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Events Sub-tabs */}
        {activeTab === 'Events' && events.length > 0 && (
          <Tabs value={eventSubTab} onValueChange={setEventSubTab} className="mb-6">
            <TabsList className="bg-white border border-[#E2E8F0] h-10">
              {events.map((event) => (
                <TabsTrigger 
                  key={event.id} 
                  value={event.name} 
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white font-semibold text-sm"
                >
                  {event.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        {/* Investment Banker Sub-tabs */}
        {activeTab === 'Investment Banker' && investmentBankers.length > 0 && (
          <Tabs value={bankerSubTab} onValueChange={setBankerSubTab} className="mb-6">
            <TabsList className="bg-white border border-[#E2E8F0] h-10 flex-wrap">
              {investmentBankers.map((banker) => (
                <TabsTrigger 
                  key={banker.id} 
                  value={banker.name} 
                  className="data-[state=active]:bg-purple-500 data-[state=active]:text-white font-semibold text-sm"
                >
                  {banker.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        {/* Fund Sub-tabs */}
        {activeTab === 'Fund' && funds.length > 0 && (
          <Tabs value={fundSubTab} onValueChange={setFundSubTab} className="mb-6">
            <TabsList className="bg-white border border-[#E2E8F0] h-10 flex-wrap">
              {funds.map((fund) => (
                <TabsTrigger 
                  key={fund.id} 
                  value={fund.name} 
                  className="data-[state=active]:bg-amber-500 data-[state=active]:text-white font-semibold text-sm"
                >
                  {fund.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        {/* Angel Network Sub-tabs */}
        {activeTab === 'Angel Network' && angelNetworks.length > 0 && (
          <Tabs value={networkSubTab} onValueChange={setNetworkSubTab} className="mb-6">
            <TabsList className="bg-white border border-[#E2E8F0] h-10 flex-wrap">
              {angelNetworks.map((network) => (
                <TabsTrigger 
                  key={network.id} 
                  value={network.name} 
                  className="data-[state=active]:bg-pink-500 data-[state=active]:text-white font-semibold text-sm"
                >
                  {network.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        {/* No subtabs message for Investment Banker */}
        {activeTab === 'Investment Banker' && investmentBankers.length === 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
            <p className="text-[#64748B] text-sm">No investment bankers have been added yet.</p>
          </div>
        )}

        {/* No subtabs message for Fund */}
        {activeTab === 'Fund' && funds.length === 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
            <p className="text-[#64748B] text-sm">No funds have been added yet.</p>
          </div>
        )}

        {/* No subtabs message for Angel Network */}
        {activeTab === 'Angel Network' && angelNetworks.length === 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-[#E2E8F0]">
            <p className="text-[#64748B] text-sm">No angel networks have been added yet.</p>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 pl-10 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748B]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-[#64748B] mt-2">
              Found {filteredDeals.length} result{filteredDeals.length !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;
            </p>
          )}
        </div>

        {/* Deals Grid */}
        {filteredDeals.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-brand to-brand-hover rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-[#0F172A] mb-3">Coming Soon</h3>
              <p className="text-[#64748B] mb-6">
                Exciting investment opportunities are on the way. Stay tuned for exclusive deals curated just for you.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F1F5F9] rounded-full">
                <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></span>
                <span className="text-sm text-[#64748B]">New deals arriving soon</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDeals.map((deal) => (
              <Card
                key={deal.id}
                data-testid={`deal-card-${deal.id}`}
                className="border-[#E2E8F0] shadow-none hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/deals/${deal.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-[#F1F5F9] rounded-md flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-brand" />
                    </div>
                    <Badge className="bg-brand text-white text-xs">{deal.type}</Badge>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#0F172A] mb-2">{deal.brand_name || deal.name}</h3>
                  <p className="text-sm text-[#64748B] mb-3 line-clamp-2">{deal.one_liner || deal.description}</p>
                  
                  {/* All Tags - Event, Investment Bankers, Funds, Angel Networks */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {/* Event Tag - hide if on Events tab viewing this event */}
                    {deal.event_category && !(activeTab === 'Events' && eventSubTab === deal.event_category) && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagClasses('events')}`}>
                        {deal.event_category}
                      </span>
                    )}
                    
                    {/* Investment Banker Tags - hide the one being viewed */}
                    {deal.investment_bankers?.filter(banker => 
                      !(activeTab === 'Investment Banker' && bankerSubTab === banker)
                    ).map((banker) => (
                      <span key={`ib-${banker}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagClasses('investment_bankers')}`}>
                        {banker}
                      </span>
                    ))}
                    
                    {/* Fund Tags - hide the one being viewed */}
                    {deal.funds?.filter(fund => 
                      !(activeTab === 'Fund' && fundSubTab === fund)
                    ).map((fund) => (
                      <span key={`fund-${fund}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagClasses('funds')}`}>
                        {fund}
                      </span>
                    ))}
                    
                    {/* Angel Network Tags - hide the one being viewed */}
                    {deal.angel_networks?.filter(network => 
                      !(activeTab === 'Angel Network' && networkSubTab === network)
                    ).map((network) => (
                      <span key={`an-${network}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTagClasses('angel_networks')}`}>
                        {network}
                      </span>
                    ))}
                  </div>
                  
                  {deal.amount_raising > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="h-4 w-4 text-[#10B981]" />
                      <span className="font-semibold text-[#10B981]">{formatINR(deal.amount_raising)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
